import * as angular from "angular"
import { IHealthySoilsAssessment, IHsaDrainage, IHsaDrillType, IHsaPrimaryCultivations, IHsaSoilSeries } from "../hsa/types"
import { INitrogenAnalysis } from "../nitrogen/types"
import { convertDatesToJsonUtc, IGenericAnalysis, ILaboratory, IOmniaContext, IPcnAnalysis, ISoilAnalysis, isUndefined, OmniaObjectType, selectMany, setDates, SoilAnalysisType } from "../omnia"
import { IInterpretationGroup, INitrogenAnalysisAllocBindingModel, INitrogenAnalysisBindingModel, ISnsAssessment, ISoilAnalysisAllocBindingModel, PropertyInterpretation } from "../types"
import { OmniaApiInterface } from "@omnia/shared/api/apiInterface"

export class SoilApi {

    private _laboratories: ILaboratory[] = []

    private _soilAnalysis: ISoilAnalysis[] = []
    private _nitrogenAnalysis: INitrogenAnalysis[] = []
    private _healthySoilsAssessments: IHealthySoilsAssessment[] = []
    private _snsAssessments: ISnsAssessment[] = []
    private _hsaSeries: IHsaSoilSeries[] = []
    private _hsaDrainage: IHsaDrainage[] = []
    private _primaryCultivations: IHsaPrimaryCultivations[] = []
    private _drillTypes: IHsaDrillType[] = []
    private _pcnAnalysis: IPcnAnalysis[] = []
    private _interpretations: IInterpretationGroup[] = []
    private _analysisOrderInProgress: boolean


    constructor(private api: OmniaApiInterface) {
    }


    public load(context: IOmniaContext): ng.IPromise<any> {

        const url = "/api/soils/soilanalysis/service/data/holding/" + context.holdingId + "/year/" + context.yearId

        return this.api.get(url).then((data: any) => {

            this._laboratories = data.Laboratories
            this._soilAnalysis = setupSoilAnalyses(data.SoilAnalysis, this._laboratories)
            this._nitrogenAnalysis = setupAnalysis<INitrogenAnalysis>(data.NitrogenAnalysis, a => a.NitrogenAnalysisId, this._laboratories)
            this._healthySoilsAssessments = setupHealthySoilsAssessments(data.HealthySoilsAssessments)
            this._snsAssessments = setupSnsAssessments(data.SnsAssessments)
            this._pcnAnalysis = setupAnalysis<IPcnAnalysis>(data.PcnAnalysis, a => a.PcnAnalysisId, null)
            this._hsaSeries = data.HsaSoilSeries
            this._hsaDrainage = data.HsaDrainage
            this._primaryCultivations = data.HsaPrimaryCultivations
            this._drillTypes = data.HsaDrillTypes
            this._interpretations = setupInterpretations(data.Interpretations)
            this._analysisOrderInProgress = data.AnalysisOrderInProgress

        })

    }

    getSoilAnalyses(filter: (sa: ISoilAnalysis) => boolean) {
        return this._soilAnalysis.filter(filter)
    }

    hasSsmAnalyses() {
        return this._soilAnalysis.some(a => a.Type == SoilAnalysisType.SSM)
    }

    getNitrogenAnalysis() {
        return this._nitrogenAnalysis.slice()
    }

    getHealthySoilsAssessments(): IHealthySoilsAssessment[] {
        return this._healthySoilsAssessments.slice()
    }

    getSnsAssessments() {
        return this._snsAssessments.slice()
    }

    getLaboratories(): ILaboratory[] {
        return this._laboratories.slice()
    }

    getHealthySoilsAssessmentSeries(): IHsaSoilSeries[] {
        return this._hsaSeries
    }

    getHsaDrainage(): IHsaDrainage[] {
        return this._hsaDrainage
    }

    getPrimaryCultivations(): IHsaPrimaryCultivations[] {
        return this._primaryCultivations
    }

    getDrillTypes(): IHsaDrillType[] {
        return this._drillTypes
    }

    getPcnAnalysis(): IPcnAnalysis[] {
        return this._pcnAnalysis
    }

    getInterpretations(): IInterpretationGroup[] {
        return this._interpretations
    }

    getAnalysisOrderInProgress(): boolean {
        return this._analysisOrderInProgress
    }

    updateSoilAnalysisAlloc(model: ISoilAnalysisAllocBindingModel): ng.IPromise<any> {

        var url = "/api/soils/soilanalysis/allocate"

        return this.api.post(url, model, { type: OmniaObjectType.SoilAnalysis })

    }

    saveSoilAnalysis(context: IOmniaContext, analysis: ISoilAnalysis, fieldsIds: number[], comments: PropertyInterpretation[]): ng.IPromise<ISoilAnalysis> {

        let url = "/api/soils/soilanalysis/save"

        let target = <ISoilAnalysis>angular.extend({}, analysis)

        target.Laboratory = null
        target.Allocations = null

        convertDatesToJsonUtc(target)

        const data = {
            SoilAnalysis: target,
            AllocFieldIds: fieldsIds,
            CroppingYearId: context.yearId,
            Interpretations: comments
        }

        return this.api.post(url, data, { type: OmniaObjectType.SoilAnalysis }).then(r => {

            let analysis = r.data as ISoilAnalysis

            if (analysis) {
                const id = (a: ISoilAnalysis) => a.SoilAnalysisId
                analysis = setupAnalysis([analysis], id, this._laboratories)[0]
                this._soilAnalysis = replaceItem(this._soilAnalysis, analysis, id)
            }

            return analysis

        })

    }

    deleteSoilAnalysis(id: number, deleteOtherGridSamples: boolean) {

        return this.deleteAnalysis("soilanalysis", id, OmniaObjectType.SoilAnalysis, deleteOtherGridSamples)

    }


    saveNitrogenAnalysis(model: INitrogenAnalysisBindingModel): ng.IPromise<number> {

        const url = "/api/soils/nitrogenanalysis/save"

        const data = <INitrogenAnalysisBindingModel>angular.extend({}, model)

        data.NitrogenAnalysis.Laboratory = null
        data.NitrogenAnalysis.Allocations = null

        convertDatesToJsonUtc(data)

        return this.api.post(url, data, { type: OmniaObjectType.NitrogenAnalysis })

    }

    updateNitrogenAnalysisAlloc(model: INitrogenAnalysisAllocBindingModel): ng.IPromise<any> {

        const url = "/api/soils/nitrogenanalysis/allocate"

        return this.api.post(url, model, { type: OmniaObjectType.NitrogenAnalysis })

    }

    deleteNitrogenAnalysis(id: number) {

        return this.deleteAnalysis("nitrogenanalysis", id, OmniaObjectType.NitrogenAnalysis)

    }

    savePcnAnalysis(context: IOmniaContext, analysis: IPcnAnalysis, fieldsIds: number[]): ng.IPromise<number> {

        const url = "/api/soils/pcn/save"

        let target = <ISoilAnalysis>angular.extend({}, analysis)

        target.Laboratory = null
        target.Allocations = null

        convertDatesToJsonUtc(target)

        let data = { PcnAnalysis: target, AllocFieldIds: fieldsIds, CroppingYearId: context.yearId }

        return this.api.post(url, data, { type: OmniaObjectType.PcnAnalysis })

    }

    deletePcnAnalysis(id: number, deleteOtherGridSamples: boolean) {

        return this.deleteAnalysis("pcn", id, OmniaObjectType.PcnAnalysis, deleteOtherGridSamples)

    }


    deleteHsa(hsaId: number): ng.IPromise<any> {

        return this.deleteAnalysis("healthysoilsassessment", hsaId, OmniaObjectType.HsAnlaysis)

    }


    private deleteAnalysis(path: string, id: number, type: OmniaObjectType, deleteOtherGridSamples?: boolean) {

        let url = `/api/soils/${path}/delete/${id}`

        if (deleteOtherGridSamples) {
            url += "/?deleteOtherGridSamples=true"
        }

        return this.api.delete(url, undefined, { type })

    }
}


function setupSoilAnalyses(analyses: ISoilAnalysis[], labs: ILaboratory[]) {

    const sa = setupAnalysis(analyses, a => a.SoilAnalysisId, labs)

    return sa.map(a => ({
        ...a,
        Type: a.Type || 0
    }))

}

function setupAnalysis<T extends IGenericAnalysis>(analysis: T[], idSelector: (a: T) => number, laboratories: ILaboratory[]): T[] {

    setDates(analysis, ["SamplingDate", "CreatedOn"], true)

    if (laboratories) {
        analysis.forEach(sa => {
            sa.Laboratory = laboratories.find(l => l.LaboratoryId == sa.LaboratoryId)
            if (isUndefined(sa.IsValid)) {
                sa.IsValid = true
            }
        })
    }

    return analysis

}

function replaceItem<T>(items: T[], updated: T, idSelector: (arg: T) => number): T[] {

    const updatedId = idSelector(updated)
    let found = false

    items = items.map(i => {
        if (idSelector(i) == updatedId) {
            found = true
            return updated
        }
        return i
    })

    if (!found) {
        items.push(updated)
    }

    return items

}

function setupInterpretations(groups: IInterpretationGroup[]) {

    const props = selectMany(groups.map(g => g.Properties))

    props.forEach(p => p.Values = (p.Values || []).sort((v1, v2) => v1.Min - v2.Min))

    return groups

}

function setupSnsAssessments(assessments: ISnsAssessment[]): ISnsAssessment[] {
    setDates(assessments, ["CalcDate"], false)
    return assessments
}

function setupHealthySoilsAssessments(assessments: IHealthySoilsAssessment[]): IHealthySoilsAssessment[] {

    setDates(assessments, ["SamplingDate", "CreatedOn"], true)

    return assessments

}
